// Parses .ds-* CSS classes from styles/recipes/*.css files into DesignSystemRecipe objects
import { DesignSystemRecipe, DesignSystemRecipeProperty } from '../types';
import { symbolDisplayName } from '../content/util';

// Extracts the domain from the recipe file path or class name
// "styles/recipes/type.css" -> "type"
// ".ds-type-heading-lg" -> "type"
function extractDomain(sourcePath: string, className: string): string {
	// Prefer domain from file path: styles/recipes/<domain>.css
	const pathMatch = sourcePath.match(/styles\/recipes\/([^/]+)\.css$/);
	if (pathMatch) {
		return pathMatch[1];
	}
	// Fallback: extract from class name (first segment after .ds-)
	const classMatch = className.match(/^\.ds-([^-]+)/);
	return classMatch ? classMatch[1] : 'other';
}

// Parses a single recipe CSS file into DesignSystemRecipe objects
// Extracts .ds-* class rules, their properties, and preceding comments as descriptions
export function parseRecipes(cssContent: string, sourcePath: string): DesignSystemRecipe[] {
	const recipes: DesignSystemRecipe[] = [];
	const lines = cssContent.split('\n');

	let i = 0;
	let pendingComment = '';

	while (i < lines.length) {
		const line = lines[i].trim();

		// Accumulate block comments as description for the next rule
		if (line.startsWith('/*')) {
			// Collect full comment (may span multiple lines)
			let comment = '';
			if (line.includes('*/')) {
				// Single-line comment
				comment = line
					.replace(/\/\*\s*/, '')
					.replace(/\s*\*\//, '')
					.trim();
			} else {
				// Multi-line comment
				comment = line.replace(/\/\*\s*/, '').trim();
				i++;
				while (i < lines.length) {
					const commentLine = lines[i].trim();
					if (commentLine.includes('*/')) {
						comment +=
							' ' +
							commentLine
								.replace(/\s*\*\//, '')
								.replace(/^\*\s*/, '')
								.trim();
						break;
					}
					comment += ' ' + commentLine.replace(/^\*\s*/, '').trim();
					i++;
				}
			}
			// Keep non-empty comments; reset if we see a blank line before a rule
			if (comment.length > 0) {
				pendingComment = pendingComment ? pendingComment + ' ' + comment : comment;
			}
			i++;
			continue;
		}

		// Blank lines reset pending comment
		if (line === '') {
			// Don't reset comment — comments can be separated by blank lines from rules
			i++;
			continue;
		}

		// Detect a CSS rule starting with .ds-* selector
		// Match single class selectors like ".ds-type-heading-lg {"
		// Skip compound selectors (containing multiple .ds- classes chained)
		const singleClassMatch = line.match(/^(\.ds-[\w-]+)\s*\{/);
		if (singleClassMatch) {
			const className = singleClassMatch[1];
			const properties: DesignSystemRecipeProperty[] = [];

			// Read properties until closing brace
			i++;
			while (i < lines.length) {
				const propLine = lines[i].trim();
				if (propLine === '}') {
					break;
				}

				// Parse CSS declaration: property: value;
				const declMatch = propLine.match(/^([\w-]+)\s*:\s*(.+?)\s*;/);
				if (declMatch) {
					properties.push({
						property: declMatch[1],
						value: declMatch[2],
					});
				}
				i++;
			}

			// Only include rules with actual properties
			if (properties.length > 0) {
				const domain = extractDomain(sourcePath, className);
				const id = className.replace(/^\.ds-/, '');

				recipes.push({
					id,
					className,
					name: symbolDisplayName(className),
					domain,
					description: pendingComment || undefined,
					properties,
					sourcePath,
				});
			}

			pendingComment = '';
			i++;
			continue;
		}

		// For compound/grouped selectors (comma-separated .ds-* classes), skip
		// These are typically modifiers applied to multiple base classes
		if (line.includes('.ds-') && line.includes('{')) {
			// Skip to closing brace
			i++;
			while (i < lines.length && !lines[i].trim().startsWith('}')) {
				i++;
			}
			pendingComment = '';
			i++;
			continue;
		}

		i++;
	}

	return recipes;
}
